import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = [
  "#58A6FF",
  "#238636",
  "#D2A8FF",
  "#79C0FF",
  "#FFA657",
  "#FF7B72",
  "#8B949E",
];

const InsightsPanel = ({
  login,
  data,
  compact,
  omitHeading,
  /** Replaces default "Insights · @{login}" when set */
  heading,
  /** Hide contribution hint paragraph */
  hideHint,
  /** "profile" uses shorter stat labels (user profile sidebar) */
  statsVariant,
  /** Stretch chart area to fill card when parent sets min-height (profile + hero match) */
  fillVertical,
}) => {
  /* Height must fit pie + bottom Legend inside ResponsiveContainer (recharts draws both in the same box). */
  const tight = compact && omitHeading;
  const chartHeight =
    statsVariant === "profile" ? 210 : tight ? 232 : compact ? 300 : 340;
  const innerRadius =
    statsVariant === "profile" ? 28 : tight ? 30 : compact ? 34 : 46;
  const outerRadius =
    statsVariant === "profile" ? 48 : tight ? 52 : compact ? 60 : 80;
  const pieCy =
    statsVariant === "profile" ? "45%" : tight ? "46%" : compact ? "44%" : "45%";

  const chartData = useMemo(() => {
    if (!data?.languages) return [];
    return Object.entries(data.languages)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data]);

  const rootClass = `gf-insights gf-card${compact ? " gf-insights--compact" : ""}${tight ? " gf-insights--tight" : ""}${statsVariant === "profile" ? " gf-insights--profile-sidebar" : ""}${fillVertical ? " gf-insights--fill-vertical" : ""}`;

  const titleNode =
    omitHeading ? null : (
      <h2 className="gf-insights__title">
        {heading || (login ? `Insights · @${login}` : "Insights")}
      </h2>
    );

  if (!login) {
    return (
      <section className={rootClass} aria-label="Insights">
        {titleNode || <h2 className="gf-insights__title">Insights</h2>}
        <p className="gf-muted">
          Run a search and pick a profile to see language mix, stars, and top
          repository.
        </p>
      </section>
    );
  }

  if (!data) {
    return (
      <section className={rootClass} aria-label="Insights">
        {titleNode}
        <div
          className={fillVertical ? "gf-insights__loading-fill" : undefined}
          aria-busy="true"
        >
          <div className="gf-skeleton gf-skeleton--chart" />
        </div>
      </section>
    );
  }

  const starsLabel =
    statsVariant === "profile" ? "Total stars" : "Total stars (sample)";
  const reposLabel =
    statsVariant === "profile" ? "Repos in sample" : "Repos analyzed";

  return (
    <section className={rootClass} aria-label="Insights">
      {titleNode}
      {!hideHint && data.contributionHint && (
        <p className="gf-insights__hint">{data.contributionHint}</p>
      )}

      <div className="gf-insights__stats">
        <div>
          <span className="gf-stat__label">{starsLabel}</span>
          <span className="gf-stat__value">{data.totalStars?.toLocaleString?.() ?? data.totalStars}</span>
        </div>
        <div>
          <span className="gf-stat__label">{reposLabel}</span>
          <span className="gf-stat__value">{data.repoCount}</span>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className={`gf-chart-wrap${fillVertical ? " gf-chart-wrap--fill" : ""}`}>
          <ResponsiveContainer width="100%" height={fillVertical ? "100%" : chartHeight}>
            <PieChart margin={{ top: 0, right: 2, bottom: 0, left: 2 }}>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy={pieCy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={2}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "var(--gf-card)",
                  border: "1px solid var(--gf-border)",
                  borderRadius: 8,
                }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingTop: tight ? 0 : 8 }}
                iconType="circle"
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : fillVertical ? (
        <div className="gf-insights__empty-chart-fill">
          <p className="gf-muted">No language data for this sample.</p>
        </div>
      ) : (
        <p className="gf-muted">No language data for this sample.</p>
      )}

      {data.topRepo && (
        <div className={`gf-top-repo${fillVertical ? " gf-top-repo--push-bottom" : ""}`}>
          <h3 className="gf-top-repo__title">
            {statsVariant === "profile" ? "Top repo" : "Most starred repository"}
          </h3>
          <a
            href={data.topRepo.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="gf-top-repo__link"
          >
            {data.topRepo.name}
          </a>
          <p className="gf-muted">
            ⭐ {data.topRepo.stargazers_count?.toLocaleString?.()}{" "}
            {data.topRepo.description && `· ${data.topRepo.description}`}
          </p>
        </div>
      )}
    </section>
  );
};

export default InsightsPanel;
